/**
 * The shared reference/history/leaderboard entry table (start-screen-unfolded-menu-strip design §
 * Structure & SRP, grown to seven rows by features-entry-points) — each `{labelKey, icon,
 * open(source)}`, covering every menu destination that isn't a game action: the five
 * reference-overlay pages (Help/Rules/Strategy/Glossary/Features), Your games, and Leaderboard.
 * `open` is a **hand-written closure over its opener's real signature** — the openers aren't
 * uniform (strategy takes `source` third; leaderboard closes over the session's current mode;
 * games/leaderboard also take the `source` param this slice adds) — so this table can't be driven
 * by a single generic call shape. Every thunk dynamic-imports its overlay module, same
 * self-wiring-inert shape `game-menu.js`'s own earlier entry table used (see
 * `reference-overlay.js`'s own header: a static import would register the reference pages'
 * `DOMContentLoaded` listeners inside this page's own boot graph).
 *
 * Two consumers on the prod path: `game-menu.js` (passes `"menu"`, renders all seven, reordered
 * into its own two sections) and the start-screen reference strip
 * (`game-start/game-start-reference-strip.js`, passes `"start"` — filters the Leaderboard row
 * out, since that screen already shows the board inline: six tiles, the strip's own
 * grid/glare-measurement constraint, features-entry-points design § 2). The animation lab reads
 * the table too, building its replica strip out of the real rows (`dev/glass-condense.js`). The
 * second prod consumer is what justifies the extraction — DRY on the knowledge unit
 * (label↔icon↔opener identity), not on text. The play-exit flight is not a third: it animates
 * the `.gs-strip-tile` nodes the strip has already rendered
 * (`game-start/game-start-strip-flight.js`), so it consumes tiles, not entries.
 */
import { getSessionConfig } from "./session-config.js";
import {
  createQuarteredShield,
  createFingerpost,
  createCompass,
  createTabbedTome,
  createLantern,
  createVictorDais,
  createKeep,
} from "./icons/nav-icons.js";

export const MENU_ENTRIES = [
  {
    labelKey: "menuHelp",
    icon: createQuarteredShield, // the app reference → a quartered shield (nav-icon-candidate-placements)
    open: (source) =>
      import("./reference-overlay.js").then((m) => m.openHelpOverlay(source)),
  },
  {
    labelKey: "menuRules",
    icon: createFingerpost, // the game reference → a crossroads signpost (nav-icon-candidate-placements)
    open: (source) =>
      import("./reference-overlay.js").then((m) => m.openRulesOverlay(source)),
  },
  {
    labelKey: "menuStrategy",
    icon: createCompass, // the skill reference → a compass, pointing the way
    open: (source) =>
      import("./reference-overlay.js").then((m) =>
        m.openStrategyOverlay(null, null, source),
      ),
  },
  {
    labelKey: "menuGlossary",
    icon: createTabbedTome, // the terms reference → a tabbed, indexed tome
    open: (source) =>
      import("./reference-overlay.js").then((m) =>
        m.openGlossaryOverlay(source),
      ),
  },
  {
    labelKey: "footerFeatures", // the family's live label key — historyTitle/leaderboardHeading's
    // precedent, no separate menuFeatures duplicate — footer*-shaped so already in the /api/i18n
    // bundle, and already the site-nav Features label (I18nController's Javadoc records this
    // reuse resolution)
    icon: createKeep, // the family's shipped mark — same identity as the site-nav entry
    open: (source) =>
      import("./reference-overlay.js").then((m) =>
        m.openFeaturesOverlay(null, source),
      ),
  },
  {
    labelKey: "historyTitle",
    icon: createLantern, // your past games → the light already carried through them (nav-icon-candidate-placements)
    open: (source) =>
      import("./games-overlay.js").then((m) => m.openGamesOverlay(source)),
  },
  {
    labelKey: "leaderboardHeading", // the surface's own title key — historyTitle's precedent, no
    // separate menuLeaderboard duplicate (leaderboard-dedicated-page-design § The overlay + menu
    // entry)
    icon: createVictorDais,
    // Closes over the session's current mode — the opener alone owns the SKAT fallback for an
    // undefined mode (e.g. opened from the start screen before any session exists).
    open: (source) =>
      import("./leaderboard/leaderboard-overlay.js").then((m) =>
        m.openLeaderboardOverlay(getSessionConfig().gameMode, source),
      ),
  },
];
